import { useState, useEffect, useCallback } from 'react';
import { Container, Badge, Spinner } from 'react-bootstrap';
import { fetchHistoricoComprasLojaFisica, fetchPedidoHistoricoLojaFisica, deletarPedidoHistoricoLojaFisica } from '../../services/api';
import { gerarPDFLojaFisica } from '../../components/ListaComprasLojaFisicaPanel/ListaComprasLojaFisicaPanel';
import { useResizableModal } from '../../hooks/useResizableModal';
import ResizableHandles from '../../components/ResizableHandles/ResizableHandles';
import styles from './HistoricoComprasLojaFisica.module.css';

const formatarMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatarData = (dt) =>
  new Date(dt).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const STATUS_CONFIG = {
  aguardando_aprovacao: { label: 'Aguardando', cor: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
  aprovado:            { label: 'Aprovado',    cor: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.3)' },
  rejeitado:           { label: 'Rejeitado',   cor: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)' },
};

const LOJA_CONFIG = {
  '001': { label: 'Centro', icon: '🏢', cor: '#6366f1', bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.3)' },
  '004': { label: 'Caxias', icon: '🏪', cor: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)' },
};

const LojaBadge = ({ loja }) => {
  const cfg = LOJA_CONFIG[loja];
  if (!cfg) return null;
  return (
    <span style={{
      background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.border}`,
      borderRadius: '12px', padding: '1px 10px', fontSize: '0.73rem', fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// ── Modal de detalhes ─────────────────────────────────────────────────────────
const ModalDetalhe = ({ pedidoId, onClose }) => {
  const [pedido, setPedido] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const { modalRef, modalStyle, resizing, iniciarResize } = useResizableModal({
    storageKey: 'historicoComprasLojaFisica',
  });

  useEffect(() => {
    fetchPedidoHistoricoLojaFisica(pedidoId).then(r => {
      if (r.success) setPedido(r.data);
      setCarregando(false);
    });
  }, [pedidoId]);

  const handleGerarPDF = async () => {
    setGerandoPDF(true);
    try { await gerarPDFLojaFisica(pedido.itens); }
    finally { setGerandoPDF(false); }
  };

  if (carregando) return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalBox}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    </div>
  );
  if (!pedido) return null;

  const cfg = STATUS_CONFIG[pedido.status] || STATUS_CONFIG.aguardando_aprovacao;

  // Agrupar por fornecedor
  const grupos = {};
  (pedido.itens || []).forEach(({ produto, quantidade }) => {
    const forn = produto?.fornecedor || 'Sem Fornecedor';
    if (!grupos[forn]) grupos[forn] = [];
    grupos[forn].push({ produto, quantidade });
  });
  const fornecedores = Object.keys(grupos).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        ref={modalRef}
        className={`${styles.modalBox} ${resizing ? styles.modalResizing : ''}`}
        style={modalStyle}
        onClick={e => e.stopPropagation()}
      >
        <ResizableHandles onResize={iniciarResize} />
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitulo}>{pedido.titulo}</h2>
            <div className={styles.modalMeta}>
              📅 {formatarData(pedido.data_criacao)}
              {pedido.usuario_nome && <span> · 👤 {pedido.usuario_nome}</span>}
              <LojaBadge loja={pedido.loja_origem} />
              <span style={{
                background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.border}`,
                borderRadius: '12px', padding: '1px 10px', fontSize: '0.75rem', fontWeight: 700, marginLeft: 6,
              }}>{cfg.label}</span>
            </div>
            {pedido.status === 'aprovado' && pedido.aprovado_por && (
              <div style={{ color: '#3b82f6', fontSize: '0.78rem', marginTop: 4 }}>
                ✅ Aprovado por <strong>{pedido.aprovado_por}</strong> em {formatarData(pedido.data_aprovacao)}
              </div>
            )}
            {pedido.status === 'rejeitado' && (
              <div style={{ color: '#f87171', fontSize: '0.78rem', marginTop: 4 }}>
                ❌ Rejeitado por <strong>{pedido.aprovado_por}</strong>
                {pedido.observacao_admin && ` — ${pedido.observacao_admin}`}
              </div>
            )}
          </div>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody}>
          {fornecedores.map(forn => {
            const itensGrupo = grupos[forn];
            const subtotal = itensGrupo.reduce(
              (acc, i) => acc + (i.produto?.precoCusto || 0) * i.quantidade * 12, 0
            );
            return (
              <div key={forn} className={styles.detalheGrupo}>
                <div className={styles.detalheGrupoHeader}>
                  <span className={styles.detalheGrupoNome}>{forn}</span>
                  <span className={styles.detalheGrupoSub}>{formatarMoeda(subtotal)}</span>
                </div>
                <table className={styles.tabelaItens}>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descrição</th>
                      <th className={styles.centro}>Dz.</th>
                      <th className={styles.centro}>Un.</th>
                      <th className={styles.direita}>Vlr. Unit.</th>
                      <th className={styles.direita}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itensGrupo.map(({ produto, quantidade }) => {
                      const cod = produto?.codigo || produto?.codigoInterno || '-';
                      const nome = produto?.nome || produto?.descricao || '-';
                      const preco = produto?.precoCusto || 0;
                      const unidades = quantidade * 12;
                      return (
                        <tr key={cod}>
                          <td className={styles.codCell}>{cod}</td>
                          <td>{nome}</td>
                          <td className={styles.centro}><strong>{quantidade}</strong></td>
                          <td className={styles.centro}>{unidades}</td>
                          <td className={styles.direita}>{preco > 0 ? formatarMoeda(preco) : '-'}</td>
                          <td className={styles.direita}>{preco > 0 ? formatarMoeda(preco * unidades) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.modalTotal}>
            <span>Total do pedido (custo estimado):</span>
            <span className={styles.modalTotalValor}>{formatarMoeda(pedido.valor_total)}</span>
          </div>

          {/* PDF apenas para aprovados */}
          {pedido.status === 'aprovado' && (
            <button className={styles.btnPDF} onClick={handleGerarPDF} disabled={gerandoPDF}>
              {gerandoPDF
                ? <><Spinner size="sm" animation="border" /> Gerando PDF...</>
                : '📄 Baixar PDF da Lista'}
            </button>
          )}

          {pedido.status === 'aguardando_aprovacao' && (
            <div className={styles.statusMsg} style={{ color: '#f59e0b' }}>
              ⏳ Aguardando aprovação do administrador para liberar o PDF.
            </div>
          )}
          {pedido.status === 'rejeitado' && (
            <div className={styles.statusMsg} style={{ color: '#f87171' }}>
              ❌ Este pedido foi rejeitado e não pode gerar PDF.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────
const HistoricoComprasLojaFisica = () => {
  const [pedidos, setPedidos] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [pedidoDetalhe, setPedidoDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await fetchHistoricoComprasLojaFisica({ limit: 100 });
    if (r.success) {
      setPedidos(r.data.filter(p => p.status !== 'aguardando_aprovacao'));
      setTotal(r.total);
    }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDeletar = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remover este pedido do histórico?')) return;
    await deletarPedidoHistoricoLojaFisica(id);
    carregar();
  };

  return (
    <Container fluid className="p-4">
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>🏪 Histórico de Compras — Loja Física</h1>
          <p className={styles.pageSub}>Pedidos aprovados — PDF disponível para download</p>
        </div>
        <div className={styles.pageStats}>
          <div className={styles.statBox}>
            <div className={styles.statNum}>{pedidos.filter(p => p.status === 'aprovado').length}</div>
            <div className={styles.statLabel}>Aprovados</div>
          </div>
          <div className={styles.statBox} style={{ borderColor: 'rgba(239,68,68,0.3)' }}>
            <div className={styles.statNum} style={{ color: '#f87171' }}>{pedidos.filter(p => p.status === 'rejeitado').length}</div>
            <div className={styles.statLabel}>Rejeitados</div>
          </div>
        </div>
      </div>

      {carregando ? (
        <div className={styles.loadingWrap}>
          <Spinner animation="border" variant="primary" />
          <span>Carregando histórico...</span>
        </div>
      ) : pedidos.length === 0 ? (
        <div className={styles.vazio}>
          <div className={styles.vazioIcone}>📭</div>
          <div className={styles.vazioTitulo}>Nenhum pedido encontrado</div>
          <div className={styles.vazioSub}>
            Os pedidos aprovados aparecerão aqui após a liberação do administrador.
          </div>
        </div>
      ) : (
        <div className={styles.lista}>
          {pedidos.map(pedido => {
            const cfg = STATUS_CONFIG[pedido.status] || STATUS_CONFIG.aprovado;
            return (
              <div
                key={pedido.id}
                className={styles.card}
                style={{ borderLeft: `4px solid ${cfg.cor}` }}
                onClick={() => setPedidoDetalhe(pedido.id)}
              >
                <div className={styles.cardLeft}>
                  <div className={styles.cardIcone}>
                    {pedido.status === 'aprovado' ? '✅' : '❌'}
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardTitulo}>{pedido.titulo}</div>
                  <div className={styles.cardMeta}>
                    <span>📅 {formatarData(pedido.data_criacao)}</span>
                    {pedido.usuario_nome && <span>· 👤 {pedido.usuario_nome}</span>}
                    <LojaBadge loja={pedido.loja_origem} />
                    <span style={{
                      background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.border}`,
                      borderRadius: '12px', padding: '1px 10px', fontSize: '0.72rem', fontWeight: 700,
                    }}>{cfg.label}</span>
                    <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                      {pedido.total_itens} {pedido.total_itens === 1 ? 'produto' : 'produtos'}
                    </Badge>
                  </div>
                  {pedido.aprovado_por && (
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.76rem', marginTop: 2 }}>
                      por {pedido.aprovado_por}
                      {pedido.observacao_admin && ` — "${pedido.observacao_admin}"`}
                    </div>
                  )}
                </div>
                <div className={styles.cardRight}>
                  {pedido.valor_total > 0 && (
                    <div className={styles.cardValor}>{formatarMoeda(pedido.valor_total)}</div>
                  )}
                  <button
                    className={styles.btnDeletar}
                    onClick={(e) => handleDeletar(pedido.id, e)}
                    title="Remover do histórico"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pedidoDetalhe && (
        <ModalDetalhe
          pedidoId={pedidoDetalhe}
          onClose={() => setPedidoDetalhe(null)}
        />
      )}
    </Container>
  );
};

export default HistoricoComprasLojaFisica;
